import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { connectionService, datasourceService, referenceService, systemService } from '../services';
import { Role, Asset, formatData, handleError } from '../helpers';
import {
  Changelog,
  DataTable,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  References,
  Tabs,
  fetchToReferencesCount,
  PrincipalLink,
} from '../components';
import {
  useCustomProperties,
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  usePrincipal,
  useNotFound,
  useMessage,
  useStatus,
} from '../hooks';

export const System = () => {
  const { queryParams } = useQueryParams();

  const [system, setSystem] = useState([]);
  const [datasources, setDatasources] = useState([]);
  const [connections, setConnections] = useState([]);
  const [fromReferences, setFromReferences] = useState([]);
  const [toReferencesCount, setToReferencesCount] = useState({});
  const [loggedInUser] = useGlobalState('loggedInUser');
  const [statuses] = useGlobalState('statuses');
  const [settings] = useGlobalState('settings');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadingConnectionsTable, setLoadingConnectionsTable] = useState(true);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { system_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { fetchPrincipals } = usePrincipal();
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.System,
    objectIds: [system_id],
  });
  const { appendStatus } = useStatus({ setState: setDatasources, assetCategory: Asset.System });

  useEffect(() => {
    fetchItems();
    fetchDatasources();
    fetchConnections();
    fetchFromReferences();

    fetchToReferencesCount({ toObjectName: Asset.System, toObjectId: system_id })
      .then(res => setToReferencesCount(res))
      .catch(err => {
        showMessage(err, 'error');
      });
  }, [system_id]);

  const fetchItems = async () => {
    systemService
      .getById(system_id)
      .then(res => {
        if (res.systems.length < 1) {
          setNotFound(true);
        } else {
          setSystem(res.systems[0]);
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      })
      .finally(res => setLoading(false));
  };

  const fetchDatasources = async () => {
    const promise = new Promise(resolve => {
      datasourceService
        .getBySystemId(system_id)
        .then(res => {
          const datasources_tmp =
            res &&
            res.datasources.map(x => {
              return {
                ...x,
                connector_filter_name:
                  x.connector_name && x.secondary_connector_name
                    ? 'both'
                    : x.connector_name || x.secondary_connector_name || 'none',
              };
            });

          setDatasources(() => datasources_tmp);
          appendStatus();
          setLoadingTable(false);
          fetchPrincipals(datasources_tmp, setDatasources);
          resolve(datasources_tmp);
        })
        .catch(err => {
          showMessage(err, 'error');
          setLoadingTable(false);
        });
    });

    Promise.all([promise, fetchFieldStatistics()])
      .then(result => {
        updateFieldStatistics(result[0], result[1]);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchConnections = async () => {
    await connectionService
      .getBySystemId(system_id)
      .then(res => {
        const result =
          res &&
          res.connections.map(x => {
            return {
              ...x,
              connector_name_tooltip:
                x.connector_code === 'odbc'
                  ? `${x.connector_name} (${x.connector_driver_name})`
                  : x.connector_name,
            };
          });
        setConnections(result);
      })
      .catch(err => {
        showMessage(err, 'error');
      })
      .finally(() => {
        setLoadingConnectionsTable(false);
      });
  };

  const fetchFromReferences = async () => {
    referenceService
      .getByFromId(Asset.System, system_id)
      .then(res => {
        setFromReferences(res.references);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchFieldStatistics = async () => {
    return new Promise(resolve => {
      datasourceService
        .getFieldStatistics(system_id)
        .then(res => {
          resolve(res.field_statistics);
        })
        .catch(() => {
          return null;
        });
    });
  };

  const updateFieldStatistics = (data, statistics) => {
    let statistic = {};
    let maxValue = 0;

    statistics.forEach(row => {
      if (row.dataset_count > maxValue) {
        maxValue = row.dataset_count;
      }
    });

    setDatasources(prev =>
      prev.map(item => {
        statistic = statistics.find(x => x.datasource_id === item.datasource_id);
        return {
          ...item,
          dataset_count: statistic ? statistic.dataset_count : 0,
          attribute_ratio: statistic ? statistic.attribute_ratio : 0,
          dataset_count_max: maxValue,
        };
      })
    );
  };

  const addDatasource = async data => {
    const datasources = { datasources: Array.isArray(data) ? data : [data] };

    await datasourceService
      .create(datasources)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchDatasources();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const addConnection = async (data, files) => {
    const connections = { connections: Array.isArray(data) ? data : [data] };

    await connectionService
      .create(connections, files)
      .then(async res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchConnections();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editSystem = async data => {
    const systems = { systems: Array.isArray(data) ? data : [data] };

    await systemService
      .update(systems)
      .then(async res => {
        fetchItems();
        fetchCustomProperties(false);
        fetchFromReferences();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editDatasource = async data => {
    const datasources = { datasources: Array.isArray(data) ? data : [data] };

    await datasourceService
      .update(datasources)
      .then(async res => {
        fetchDatasources();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editConnection = async (data, files) => {
    const connections = { connections: Array.isArray(data) ? data : [data] };

    await connectionService
      .update(connections, files)
      .then(async res => {
        fetchConnections();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteSystem = async data => {
    await systemService
      .destroy({ systems: data })
      .then(async res => {
        hideModalDialog();
        showMessage(res.message);

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/systems`);
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDatasource = async data => {
    await datasourceService
      .destroy({ datasources: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchDatasources();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteConnection = async data => {
    await connectionService
      .destroy({ connections: data })
      .then(async res => {
        fetchConnections();
        hideModalDialog();
        clearSelectedItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const data = loading ? [] : system;

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;
  const editableDatasource =
    loggedInUser && [Role.admin, Role.editor].includes(loggedInUser.user_role_name);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="System"
          title={
            <span>
              {data.system_name}
              {data.system_name !== data.system_code && (
                <span className="light-text">{data.system_code}</span>
              )}
            </span>
          }
          statusId={data.status_id}
          subtitleText={data && !loading && data.system_type}
          details={[
            ...renderCustomPropertyValues().details,
            { title: 'Id', data: data.system_id },
            { title: 'Last Modified', data: formatData(data.modified_timestamp, 'datetime') },
            {
              title: 'Created',
              data: formatData(data.created_timestamp, 'datetime'),
              tooltip: 'Katalogue-internal id for this asset',
            },
          ]}
          description={data.system_description}
          fromReferences={fromReferences}
          loading={loading}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editSystem', [data], editSystem),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteSystem', [data], deleteSystem),
            },
          ]}
        >
          <ObjectDetail label="Owner" loading={loading}>
            <PrincipalLink principalId={data.owner_principal_id} loading={loading} />
          </ObjectDetail>

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
          disableTabsWithoutResults={true}
        >
          <div
            label="Datasources"
            tabid="datasources"
            resultCount={loadingTable || datasources.length === 0 ? undefined : datasources.length}
          >
            <DataTable
              columns={[
                {
                  id: 'datasource_type_category',
                  name: '',
                  type: 'icon',
                  tooltip: 'datasource_type_name',
                },
                {
                  id: 'datasource_name',
                  name: 'Name',
                  link: '/systems/:system_id/:datasource_id',
                  tooltip: 'datasource_description',
                  className: 'bold',
                },
                {
                  id: 'datasource_type_name',
                  name: 'Type',
                  className: 'small-text',
                  viewMode: 'statistics',
                },
                {
                  id: 'datasource_description',
                  name: 'Description',
                  type: 'description',
                  viewMode: 'descriptions',
                },
                {
                  id: 'dataset_count',
                  name: 'Datasets',
                  type: 'chart',
                  chartType: 'bar',
                  data: { data: 'dataset_count', max: 'dataset_count_max' },
                  viewMode: 'statistics',
                },
                {
                  id: 'attribute_ratio',
                  name: 'Documentation Level',
                  type: 'chart',
                  chartType: 'percent-bar',
                  data: { data: 'attribute_ratio' },
                  viewMode: 'statistics',
                },
                ...(settings.SHOW_SYSTEM_STATUSES
                  ? [
                      {
                        id: 'status_name',
                        name: 'Status',
                        type: 'pill',
                        value: 'status_id',
                        values: statuses,
                        tooltip: 'status_description',
                      },
                    ]
                  : []),
                {
                  id: 'principal_name',
                  name: 'Owner',
                  type: 'owner',
                  link: '/:principal_url',
                  data: { photo: 'principal_photo' },
                  tooltipSubtitle: 'principal_type_name',
                },
              ]}
              buttons={[
                {
                  label: '+ Add',
                  action: 'add',
                  mainButton: true,
                  onClick: items =>
                    showSideDialog('addDatasource', items || selectedItems, addDatasource),
                },
                {
                  label: 'Edit',
                  action: 'edit',
                  onClick: items =>
                    showSideDialog('editDatasource', items || selectedItems, editDatasource),
                },
                {
                  label: 'Delete',
                  action: 'delete',
                  onClick: items =>
                    showModalDialog('deleteDatasource', items || selectedItems, deleteDatasource),
                },
              ]}
              data={datasources}
              idColumn="datasource_id"
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              loading={loadingTable}
              editable={editableDatasource}
              filterable={true}
              filterObjects={[
                'datasource_name',
                'datasource_description',
                'datasource_type_category',
                'datasource_type_name',
                ...(settings.SHOW_SYSTEM_STATUSES ? ['status_name'] : []),
                'principal_name',
                'connector_filter_name',
              ]}
              showViewModes={true}
            />
          </div>

          {loggedInUser.user_role_name === Role.admin && (
            <div
              label="Connections"
              tabid="connections"
              resultCount={
                (loadingConnectionsTable || connections.length) === 0
                  ? undefined
                  : connections.length
              }
            >
              <DataTable
                columns={[
                  {
                    id: 'connector_code',
                    name: '',
                    type: 'icon',
                    tooltip: 'connector_name_tooltip',
                  },
                  {
                    id: 'connection_name',
                    name: 'Name',
                    link: '/connections/:connection_id',
                    tooltip: 'connection_description',
                    className: 'bold',
                  },
                  {
                    id: 'connector_name',
                    name: 'Connector',
                    className: 'small-text',
                    viewMode: 'statistics',
                  },
                  {
                    id: 'connection_description',
                    name: 'Description',
                    type: 'description',
                    viewMode: 'descriptions',
                  },
                  { id: 'connection_hostname', name: 'Hostname', viewMode: 'statistics' },
                  { id: 'connection_port', name: 'Port', viewMode: 'statistics' },
                  { id: 'connection_database', name: 'Database', viewMode: 'statistics' },
                ]}
                buttons={[
                  {
                    label: '+ Add',
                    action: 'add',
                    mainButton: true,
                    onClick: items =>
                      showSideDialog('addConnection', items || selectedItems, addConnection),
                  },
                  {
                    label: 'Edit',
                    action: 'edit',
                    onClick: items =>
                      showSideDialog('editConnection', items || selectedItems, editConnection),
                  },
                  {
                    label: 'Delete',
                    action: 'delete',
                    onClick: items =>
                      showModalDialog('deleteConnection', items || selectedItems, deleteConnection),
                  },
                ]}
                data={connections}
                idColumn="connection_id"
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                loading={loadingConnectionsTable}
                editable={editable}
                filterable={true}
                filterObjects={[
                  'connection_name',
                  'connection_description',
                  'connector_name',
                  'connector_category',
                  'connection_hostname',
                  'connection_database',
                  'system_name',
                ]}
                showViewModes={true}
              />
            </div>
          )}

          <div
            label="Related Assets"
            tabid="related_assets"
            resultCount={toReferencesCount && toReferencesCount.total}
          >
            <References
              showMessage={showMessage}
              toObjectName={Asset.System}
              toObjectId={system_id}
              toReferencesCount={toReferencesCount}
            />
          </div>

          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.System}
              objectId={system_id}
              filterObjects={[
                'changedData.new_data||old_data.system_name',
                'changedData.new_data||old_data.system_description',
                'changedData.new_data||old_data.system_type',
                'changedData.new_data||old_data.principal_name',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
